import { useState } from 'react';
import {
  trackUserAction,
  type TalentMatchingAttribution,
  type TrackedFilter,
} from 'talent_matching/lib/tracker';
import type { ConfigurationAssistantFilters } from 'talent_matching/types';
import type { ConfigurationAssistantResponse } from './use_configuration_assistant_chat';
import type {
  AssistantResponseReasons,
  AssistantResponseVote,
} from './assistant_response_feedback';

type Params = {
  attribution: TalentMatchingAttribution;
  trackedFilters: (filters: ConfigurationAssistantFilters) => TrackedFilter[];
};

const responsePayload = (response: ConfigurationAssistantResponse) => ({
  chat_id: response.chatId,
  assistant_message_id: response.id,
  model: response.model,
  model_version: response.modelVersion,
});

// Votes are capture-only (Snowplow); they live for the chat session, which a
// page load already resets.
export const useAssistantResponseVotes = ({
  attribution,
  trackedFilters,
}: Params) => {
  const [votes, setVotes] = useState<Record<number, AssistantResponseVote>>({});

  const voteOn = (
    response: ConfigurationAssistantResponse,
    next: AssistantResponseVote | null
  ) => {
    const previous = votes[response.id] ?? null;
    setVotes((current) => {
      const updated = { ...current };
      if (next) {
        updated[response.id] = next;
      } else {
        delete updated[response.id];
      }
      return updated;
    });

    const filters = response.proposal
      ? trackedFilters(response.proposal.filters)
      : [];
    trackUserAction({
      ...attribution,
      category: 'interaction',
      action: 'assistant_response_voted',
      filters: filters.length > 0 ? filters : undefined,
      metadata: {
        ...responsePayload(response),
        vote: next,
        previous_vote: previous,
        calibration: response.proposal?.calibration_settings ?? null,
      },
    });
  };

  const submitReasons = (
    response: ConfigurationAssistantResponse,
    vote: AssistantResponseVote,
    reasons: AssistantResponseReasons
  ) => {
    trackUserAction({
      ...attribution,
      category: 'interaction',
      action: 'assistant_response_feedback_submitted',
      metadata: {
        ...responsePayload(response),
        vote,
        feedback: reasons.feedback,
        comment: reasons.comment ?? null,
      },
    });
  };

  return { votes, voteOn, submitReasons };
};
